import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {translate} from 'views/module';

import AssigneePicker from './components/AssigneePicker/AssigneePicker';
import TaskTextFields from './components/TaskTextFields';
import DatePickerQuickOptionCalendar from '../dates/DatePickerQuickOptionCalendar';

import backend from 'utils/backend';
import moment from 'moment';
import _ from 'lodash';

const TaskEditor = (props) => {
  const {
    task,
    isEditing,
    onCancel,
    onSubmit,
  } = props;

  const [title, setTitle] = React.useState('');
  const [taskDescription, setTaskDescription] = React.useState('');
  const [taskDueDate, setTaskDueDate] = React.useState('');
  const [isDueDateOpen, setIsDueDateOpen] = React.useState(false);
  const [staticOwners, setStaticOwners] = React.useState([]);
  const [staticConnections, setStaticConnections] = React.useState([]);
  const [textOwners, setTextOwners] = React.useState([]);
  const [textConnections, setTextConnections] = React.useState([]);
  const [type, setType] = React.useState(null);
  const [admins, setAdmins] = React.useState([]);
  const [caregivers, setCaregivers] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [commandContext, setCommandContext] = React.useState(null);
  const [isCommandLoading, setIsCommandLoading] = React.useState(false);
  const dueDateButtonRef = React.useRef(null);

  const hasMentionToken = (textValue, prefix, display) => {
    if (!textValue) {
      return false;
    }

    const escapedDisplay = display.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|\\s)\\${prefix}${escapedDisplay}(?=$|[\\s,.;:!?\\)])`, 'i');
    return pattern.test(textValue);
  };

  React.useEffect(() => {
    const mentionText = `${task?.title || ''}\n${task?.description?.blob || ''}`;

    setTaskDueDate(task?.dueDate || moment().endOf('day').utc().toISOString());
    if (task?.title) {
      setTitle(task.title);
    }
    if (task?.description?.blob) {
      setTaskDescription(task.description.blob);
    }
    if (task?.type) {
      setType(task.type);
    }
    if (task?.ownerInfo) {
      setTextOwners((task.ownerInfo || [])
          .map((owner) => ({...owner, ownerRn: owner.ownerRn}))
          .filter((owner) => hasMentionToken(mentionText, '@', `${owner?.firstName} ${owner?.lastName || ''}`.trim())));
      setStaticOwners((task.ownerInfo || [])
          .map((owner) => ({...owner, ownerRn: owner.ownerRn}))
          .filter((owner) => !hasMentionToken(mentionText, '@', `${owner?.firstName} ${owner?.lastName || ''}`.trim())));
    }
    if (task?.connectionInfo) {
      const taskConnections = (task.connectionInfo || [])
          .map((connection) => ({...connection, connectionRn: connection.connectionRn}))
          .filter((connection) => Boolean(connection.connectionRn));

      setTextConnections(taskConnections
          .filter((connection) => {
            const display = `${connection.firstName} ${connection.lastName || ''}`.trim();
            return hasMentionToken(mentionText, '#', display) || hasMentionToken(mentionText, '$', display);
          }));
      setStaticConnections(taskConnections
          .filter((connection) => {
            const display = `${connection.firstName} ${connection.lastName || ''}`.trim();
            return !hasMentionToken(mentionText, '#', display) && !hasMentionToken(mentionText, '$', display);
          }));

      setCaregivers(taskConnections.map((connection) => ({
        caregiverRn: connection.connectionRn,
        connectionRn: connection.connectionRn,
        caregiverName: `${connection.firstName} ${connection.lastName || ''}`.trim(),
      })));

      setClients(taskConnections.map((connection) => ({
        clientRn: connection.connectionRn,
        connectionRn: connection.connectionRn,
        clientName: `${connection.firstName} ${connection.lastName || ''}`.trim(),
      })));
    }
  }, [task]);

  React.useEffect(() => {
    if (textOwners.length === 0) {
      return;
    }

    setStaticOwners((previousOwners) => {
      const nextOwners = previousOwners.filter((owner) => {
        const ownerRn = owner?.userRn || owner?.ownerRn;
        return !textOwners.some((textOwner) => (textOwner?.userRn || textOwner?.ownerRn) === ownerRn);
      });

      return nextOwners.length === previousOwners.length ? previousOwners : nextOwners;
    });
  }, [textOwners]);

  React.useEffect(() => {
    if (textConnections.length === 0) {
      return;
    }

    setStaticConnections((previousConnections) => {
      const nextConnections = previousConnections.filter((connection) => {
        const connectionRn = connection?.connectionRn;
        return !textConnections.some((textConnection) => textConnection?.connectionRn === connectionRn);
      });

      return nextConnections.length === previousConnections.length ? previousConnections : nextConnections;
    });
  }, [textConnections]);

  const assigneeCount = staticOwners.length + staticConnections.length + textOwners.length + textConnections.length;

  const dueDateDisplayLabel = React.useMemo(() => {
    const parsedValue = taskDueDate ? moment(taskDueDate) : null;

    if (!parsedValue?.isValid?.()) {
      return translate('tasks.Common.TaskEditor.Date');
    }

    return parsedValue.local().format('ddd MMM D h:mmA');
  }, [taskDueDate]);

  const handleDueDateChange = (value) => {
    setTaskDueDate(value || '');
  };

  const handleTitleChange = (value) => {
    setTitle(value || '');
  };

  const handleDescriptionChange = (value) => {
    setTaskDescription(value || '');
  };

  const handleSelectAssignee = (entityType, entity) => {
    let selectedLabel = entity?.name || '';

    if (entityType === 'admins') {
      const adminRn = entity?.userRn;
      const adminName = entity?.name || `${entity?.firstName || ''} ${entity?.lastName || ''}`.trim();

      setAdmins((previousAdmins) => (
          previousAdmins.some((admin) => admin?.userRn === adminRn) ?
            previousAdmins :
            [...previousAdmins, entity]
      ));

      setTextOwners((previousOwners) => (
          previousOwners.some((owner) => (owner?.userRn || owner?.ownerRn) === adminRn) ?
            previousOwners :
            [...previousOwners, {...entity, userRn: adminRn}]
      ));

      selectedLabel = adminName;
    }

    if (entityType === 'caregivers') {
      const caregiverRn = entity?.caregiverRn;
      const caregiverName = entity?.caregiverName;

      setCaregivers((previousCaregivers) => (
          previousCaregivers.some((caregiver) => caregiver?.caregiverRn === caregiverRn) ?
            previousCaregivers :
            [...previousCaregivers, entity]
      ));

      setTextConnections((previousConnections) => (
          previousConnections.some((connection) => connection?.connectionRn === caregiverRn) ?
            previousConnections :
            [...previousConnections, {connectionRn: caregiverRn, name: caregiverName, type: 'caregiver'}]
      ));

      selectedLabel = caregiverName;
    }

    if (entityType === 'clients') {
      const clientRn = entity?.clientRn;
      const clientName = entity?.clientName;

      setClients((previousClients) => (
          previousClients.some((client) => client?.clientRn === clientRn) ?
            previousClients :
            [...previousClients, entity]
      ));

      setTextConnections((previousConnections) => (
          previousConnections.some((connection) => connection?.connectionRn === clientRn) ?
            previousConnections :
            [...previousConnections, {connectionRn: clientRn, name: clientName, type: 'client'}]
      ));

      selectedLabel = clientName;
    }

    const commandPrefixByType = {
      admins: '@',
      caregivers: '#',
      clients: '$',
    };

    const commandPrefix = commandPrefixByType[entityType];
    const token = `${commandPrefix}${selectedLabel}`;
    const descriptionLower = taskDescription.toLowerCase();

    if (descriptionLower.includes(token.toLowerCase())) {
      return;
    }

    const spacer = taskDescription && !/\s$/.test(taskDescription) ? ' ' : '';
    handleDescriptionChange(`${taskDescription}${spacer}${token} `);
  };

  const handleSubmitTask = () => {
    if (!title || !moment(taskDueDate).isValid()) {
      return;
    }

    const ownerRns = [...new Set(
        [...staticOwners, ...textOwners]
            .map((owner) => owner.userRn || owner.ownerRn),
    )];
    const connectionRns = [...new Set(
        [...staticConnections, ...textConnections]
            .map((connection) => connection.connectionRn),
    )];

    const payload = {
      title: title.trim(),
      description: {
        blob: taskDescription.trim(),
        editorType: 'TEXT',
      },
      dueDate: taskDueDate,
      ownerRns,
      connections: connectionRns,
      type,
      status: task?.status,
      skillRn: task?.skillRn || null,
    };
    onSubmit(payload);
  };

  const handleEditorKeyDown = (event) => {
    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitTask();
    }
  };

  const hasDueDate = Boolean(taskDueDate && moment(taskDueDate).isValid());
  const canSubmit = Boolean(title) && moment(taskDueDate).isValid();

  React.useEffect(() => {
    let mounted = true;

    const loadAdmins = async () => {
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);

        if (mounted) {
          setAdmins(result?.list ?? []);
        }
      } catch (error) {
        console.error('Failed to load admin users', error);
      }
    };

    loadAdmins();

    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (commandContext?.trigger !== '#') {
      return;
    }

    const searchQuery = commandContext?.query || '';
    const isFirstLoad = searchQuery.length === 0;
    let mounted = true;
    setIsCommandLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const filter = [];

        if (searchQuery) {
          filter.push({key: 'caregiverName', value: `${searchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.caregiver.queryCaregiver(filter, sortBy);

        if (mounted) {
          const nextCaregivers = result.list || [];
          setCaregivers((previousCaregivers) => _.uniqBy(
              [...nextCaregivers, ...previousCaregivers],
              (caregiver) => caregiver.caregiverRn,
          ));
        }
      } catch (error) {
        console.error('Failed to load caregivers for commands', error);
      } finally {
        if (mounted) {
          setIsCommandLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [commandContext?.query, commandContext?.trigger]);

  React.useEffect(() => {
    if (commandContext?.trigger !== '$') {
      return;
    }

    const searchQuery = commandContext?.query || '';
    const isFirstLoad = searchQuery.length === 0;
    let mounted = true;
    setIsCommandLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const filter = [
          {
            key: 'status',
            value: [
              'ACTIVE',
              'LEAD',
              'DETAILS_NEEDED',
              'CG_SEARCH',
              'CG_SEARCH_ACTIVE',
              'CG_PROFILE_SENT',
              'MEET_GREET',
              'STALLED',
            ],
          },
        ];

        if (searchQuery) {
          filter.push({key: 'clientName', value: `${searchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.client.queryClient(filter, sortBy);

        if (mounted) {
          const nextClients = result.list || [];
          setClients((previousClients) => _.uniqBy(
              [...nextClients, ...previousClients],
              (client) => client.clientRn,
          ));
        }
      } catch (error) {
        console.error('Failed to load clients for commands', error);
      } finally {
        if (mounted) {
          setIsCommandLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [commandContext?.query, commandContext?.trigger]);

  return (
    <Box sx={{position: 'relative'}}>
      <Stack
        onKeyDown={handleEditorKeyDown}
        spacing={1.5}
        sx={{
          border: '1px solid #E8E4DE',
          borderRadius: '10px',
          p: 1.5,
          backgroundColor: '#FFFFFF',
        }}
      >
        <TaskTextFields
          title={title}
          taskDescription={taskDescription}
          admins={admins}
          caregivers={caregivers}
          clients={clients}
          onDueDateChange={handleDueDateChange}
          onTitleChange={handleTitleChange}
          onDescriptionChange={handleDescriptionChange}
          onOwnersChange={setTextOwners}
          onConnectionsChange={setTextConnections}
          onTypeChange={setType}
          onCommandContextChange={setCommandContext}
          commandLoading={isCommandLoading}
        />
        <Box
          sx={{
            borderTop: '1px solid #E8E4DE',
            pt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              anchorref={dueDateButtonRef}
              onClick={() => setIsDueDateOpen(true)}
              startIcon={<CalendarTodayOutlined sx={{fontSize: 16}} />}
              sx={{
                'borderColor': '#E8E4DE',
                'color': hasDueDate ? '#2563EB' : '#7A756E',
                'backgroundColor': 'transparent',
                'borderRadius': '8px',
                'textTransform': 'none',
                'minWidth': 0,
                'px': 1.5,
                'py': 0.5,
                '& .MuiButton-startIcon': {
                  color: hasDueDate ? '#2563EB' : '#A8A49E',
                },
              }}
            >
              {dueDateDisplayLabel}
            </Button>
            <DatePickerQuickOptionCalendar
              anchorRef={dueDateButtonRef}
              open={isDueDateOpen}
              onClose={() => setIsDueDateOpen(false)}
              onChange={handleDueDateChange}
              value={taskDueDate}
            />
            <AssigneePicker
              assigneeCount={assigneeCount}
              onSelectAssignee={handleSelectAssignee}
            />
          </Stack>
          <Stack direction='row' spacing={1} sx={{ml: 'auto'}}>
            <Button
              variant='text'
              onClick={onCancel}
              sx={{
                color: '#7A756E',
                textTransform: 'none',
              }}
            >
              {translate('tasks.Common.TaskEditor.Cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmitTask}
              disabled={!canSubmit}
              sx={{
                backgroundColor: '#DA7955',
                color: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              {isEditing ? translate('tasks.Common.TaskEditor.SaveTask') : translate('tasks.Common.TaskEditor.AddTask')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

TaskEditor.propTypes = {
  task: PropTypes.object,
  isEditing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default React.memo(TaskEditor);
